import * as React from "react";
import { withStyles, CssBaseline, createMuiTheme, MuiThemeProvider, withWidth } from "@material-ui/core";
import { connect } from "react-redux";
import { StoreState } from "./store/storeState";
import { createStyles, Theme, WithStyles } from "@material-ui/core/styles";
import { TitleBar } from "./types/titleBar";
import { withRouter } from "react-router-dom";
import { RouteComponentProps } from "react-router";
import { CustomerState } from "./types/authentication";
import { ThunkDispatch } from "redux-thunk";
import { AnyAction } from "redux";
import { loadAppSettings } from "../common/actions/appSettingsActions";
import AppWideErrorModal from "../common/Components/general/AppWideErrorModal";
import AppDrawer from "./components/Drawer/AppDrawer";
import ApptsAppBar from "./components/common/ApptsAppBar";
import { appBarHeight, getCustomTheme } from "../customTheme";
import ContentCircularProgress from "src/common/Components/general/ContentCircularProgress";
import { hexToRgb, RGB } from "./utilities/colorUtil";
import { CustomPalette } from "../common/types/appSettings";
import Routes from "./Routes";
import { internalNavItems, defaultNavItems, allNavItems } from "./navItems";
import ConfirmDialog from "src/common/Components/ConfirmDialog";
import { logout } from "./actions/authActions";
import MarketingLinkActions from "./actions/marketingLinkActions";
import * as qs from "query-string";
import SurveyLinksApi from "./api/surveyLinkApi";
import { closeAppAfterAppt } from "./actions/appActions";
import Div100vh from "react-div-100vh";
import { WithWidth, isWidthUp } from "@material-ui/core/withWidth";

interface AppState {
  title: TitleBar;
  drawerOpen: boolean;
  configurationLoading: boolean;
  appTheme?: Theme;
  primaryRgbColor: RGB;
  confirmationDialogOpen: boolean;
}

interface AppReduxProps {
  appTitle: TitleBar;
  authentication: CustomerState;
  palette: CustomPalette;
  isInternal: boolean;
}

interface AppDispatchProps {
  init: () => Promise<void>;
  resolveDeepLink: (id: string) => Promise<void>;
  logout: () => void;
  setCloseAfterAppt: () => void;
}

interface AppOwnProps extends RouteComponentProps<any> { }

interface AppProps extends AppReduxProps, AppOwnProps, AppDispatchProps, WithStyles<typeof styles>, WithWidth {
  classes: any;
}

class App extends React.Component<AppProps, AppState> {
  private _isMounted: boolean = false;
  private timeoutId: NodeJS.Timeout | null = null;

  static getDerivedStateFromProps(nextProps: AppProps, prevState: AppState) {
    if (
      (nextProps.appTitle && prevState.title.title !== nextProps.appTitle.title) ||
      prevState.appTheme === undefined
    ) {
      const newState: Partial<AppState> = {
        title: {
          description: nextProps.appTitle.description,
          title: nextProps.appTitle.title
        }
      };

      if (prevState.appTheme === undefined && nextProps.palette.primary.main !== "") {
        newState.appTheme = createMuiTheme(getCustomTheme(nextProps.palette));
        newState.primaryRgbColor = hexToRgb(nextProps.palette.primary.main);
      }

      return newState;
    } else {
      return null;
    }
  }
  constructor(props: AppProps) {
    super(props);
    this.state = {
      configurationLoading: true,
      title: {
        description: "",
        title: ""
      },
      drawerOpen: false,
      primaryRgbColor: {
        r: 0,
        g: 0,
        b: 0
      },
      confirmationDialogOpen: false
    };
  }

  async componentDidMount() {
    this._isMounted = true;
    // try to hide the app
    const ua = window.navigator.userAgent.toLowerCase();
    const isIPad = !!ua.match(/(ipad)( simulator)?;/i);
    const isIPhone = !!ua.match(/(iphone|ipod)( simulator)?;/i);
    if (isIPhone || isIPad) {
      let scrollBit = isIPhone ? 1 : 0;
      this.timeoutId = setTimeout(() => {
        if (this._isMounted) {
          window.scrollTo(0, scrollBit);
        }
      }, 0);
    }
    if (this._isMounted) {
      this.setState({ configurationLoading: true });
    }
    if (location.search && location.search.length > 0) {
      const parsed: any = qs.parse(location.search);
      if (parsed.l) {
        this.props.resolveDeepLink(parsed.l.toString());
      } else if (parsed.s) {
        const link = await SurveyLinksApi.getLink(parsed.s.toString());
        this.props.history.push(`/survey/${link.appointmentId}`);
      }
    }

    // set close after appt
    if (this.props.location.search) {
      const params = new URLSearchParams(this.props.location.search);
      const closeAfterAppt = params.get("closeAfterAppt");
      if (closeAfterAppt === "true") {
        this.props.setCloseAfterAppt();
      }
    }

    await this.props.init();
    if (this._isMounted) {
      this.setState({ configurationLoading: false });
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  openDrawer = () => {
    this.setState({ drawerOpen: true });
  };

  closeDrawer = () => {
    this.setState({ drawerOpen: false });
  };

  onCancelClick = () => {
    this.setState({ confirmationDialogOpen: true });
  };

  closeConfirmModal = () => {
    this.setState({ confirmationDialogOpen: false });
  };

  routeToHome = () => {
    this.setState({ confirmationDialogOpen: false });
    if (!this.props.authentication.isCustomer && this.props.location.pathname.indexOf("survey") == -1) {
      this.closeDrawer();
      this.props.logout();
    } else {
      this.closeDrawer();
      this.props.history.push("/");
    }
  };

  public render() {
    const { classes, authentication, isInternal, width } = this.props;
    const { drawerOpen, title, configurationLoading, appTheme, primaryRgbColor, confirmationDialogOpen } = this.state;
    if (!authentication.isSignedIn && drawerOpen) {
      this.closeDrawer();
    }
    let cancelTooltipText = "",
      confirmDialogContent = "",
      confirmDialogTitle = "",
      showCancel = false,
      isSurvey = false;
    if (this.props.location.pathname.indexOf("survey") > -1) {
      isSurvey = true;
      showCancel = true;
      cancelTooltipText = "Exit Survey";
      confirmDialogTitle = "Exit Survey";
      confirmDialogContent = "Do you wish to exit the feedback survey?";
    } else if (
      this.props.location.pathname.indexOf("appt") > -1 ||
      (!authentication.isCustomer && this.props.location.pathname.indexOf("help") == -1)
    ) {
      showCancel = true;
      cancelTooltipText = "Cancel Appointment Creation";
      confirmDialogTitle = "Cancel Appointment";
      confirmDialogContent = "Do you wish to cancel the appointment creation wizard?";
    }
    // return all nav items when the user is not signed in
    // This allows us to deep link and re-route to the login page
    const navItems = !authentication.isSignedIn
      ? allNavItems
      : !authentication.isCustomer
        ? defaultNavItems
        : isInternal
          ? internalNavItems
          : allNavItems;

    return appTheme ? (
      <MuiThemeProvider theme={appTheme}>
        <div className={classes.root}>
          <CssBaseline />
          {configurationLoading ? (
            <ContentCircularProgress />
          ) : (
            <div>
              {(authentication.isSignedIn || isSurvey) && (
                <ApptsAppBar
                  onIconClick={this.openDrawer}
                  customerState={authentication}
                  title={title}
                  primaryRgbColor={primaryRgbColor}
                  onCancelClick={this.onCancelClick}
                  showCancelButton={showCancel}
                  tooltipText={cancelTooltipText}
                />
              )}
              <AppDrawer open={drawerOpen} closeDrawer={this.closeDrawer} navItems={navItems} />
              <Div100vh
                style={{
                  height: `calc(100rvh - ${isSurvey ? appBarHeight.sm : !isWidthUp("sm", width, false) ? appBarHeight.gtSm : appBarHeight.sm
                    }rem)`
                }}
                className={classes.mainContent}
              >
                <Routes navItems={navItems} />
              </Div100vh>
            </div>
          )}
          <AppWideErrorModal />
          {confirmationDialogOpen && (
            <ConfirmDialog
              titleText={confirmDialogTitle}
              open={confirmationDialogOpen}
              contentText={confirmDialogContent}
              onConfirmClick={this.routeToHome}
              onCancelClick={this.closeConfirmModal}
              confirmButtonText="Yes"
              cancelButtonText="No"
            />
          )}
        </div>
      </MuiThemeProvider>
    ) : (
      <ContentCircularProgress ignorePrimaryColor={true} />
    );
  }
}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      // iOS Safari specific to enable smooth-scrolling/inertia
      WebkitOverflowScrolling: "touch"
    },
    mainContent: {
      margin: "0 auto",
      marginTop: `${appBarHeight.sm}rem`,
      [theme.breakpoints.up("md")]: {
        marginTop: `${appBarHeight.gtSm}rem`
      }
    }
  });

const mapStateToProps = (state: StoreState, ownProps: AppOwnProps): AppReduxProps & AppOwnProps => {
  return {
    ...ownProps,
    appTitle: state.appTitleBar,
    authentication: state.authentication,
    palette: state.appSettings.palette,
    isInternal: state.appSettings.isInternal
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<StoreState, void, AnyAction>): AppDispatchProps => {
  return {
    init: async () => {
      await Promise.all([await dispatch(await loadAppSettings())]);
    },
    resolveDeepLink: async (id: string) => {
      await dispatch(await MarketingLinkActions.getDeepLink(id));
    },
    logout: () => dispatch(logout),
    setCloseAfterAppt: () => dispatch(closeAppAfterAppt())
  };
};

export default withStyles(styles)(
  withWidth()(
    withRouter(
      connect(
        mapStateToProps,
        mapDispatchToProps
      )(App)
    )
  )
);
